import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PortalApiService from '../../../services/PortalApiService';
import Loader from '../../../components/Loader';
import PageHeader from '../../../components/ui/PageHeader';
import StatusBadge from '../../../components/ui/StatusBadge';
import Avatar from '../../../components/ui/Avatar';
import { formatDate } from '../../../utils/helper';
import { ACTION_LABELS, confirmStatusChange } from './List';

const currency = process.env.REACT_APP_CURRENCY || 'AED';
const money = (v) => (v != null ? `${currency} ${Number(v).toFixed(2)}` : '—');

const Row = ({ label, children }) => (
    <div className="d-flex justify-content-between gap-4 py-2 border-bottom border-gray-100">
        <span className="text-muted">{label}</span>
        <span className="text-dark text-end fw-semibold">{children || '—'}</span>
    </div>
);

const PortalBookingDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [booking, setBooking] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        PortalApiService.request({ method: 'GET', url: `/bookings/${id}` })
            .then(({ data }) => {
                if (data.status) setBooking(data.data);
                else { toast.error(data.message); navigate('/bookings'); }
            })
            .catch(() => navigate('/bookings'));
    }, [id]);

    const changeStatus = async (status) => {
        try {
            setIsUpdating(true);
            const updated = await confirmStatusChange(booking, status);
            if (updated) setBooking(updated);
        } catch (error) {
            // PortalApiService already shows the error toast
        } finally {
            setIsUpdating(false);
        }
    };

    if (!booking) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Loader text="Loading booking..." size="40" />
            </div>
        );
    }

    const service = booking.service_id || {};
    const customer = booking.user_id || {};
    const next = booking.next_statuses || [];
    // Facility bookings happen at the customer's property (address holds a snapshot of it from booking time)
    const property = booking.property_id || (booking.location === 'Property' ? booking.address : null);
    const address = property ? null : booking.address && typeof booking.address === 'object'
        ? Object.values(booking.address).filter(v => typeof v === 'string' && v).join(', ')
        : booking.address;

    return (
        <div className="hp-page">
            <Link to="/bookings" className="text-muted fs-7 d-inline-flex align-items-center gap-1 mb-3">
                <i className="bi bi-arrow-left"></i> Back to bookings
            </Link>
            <PageHeader
                title={<span className="d-inline-flex align-items-center gap-3">Booking #{booking.job_code} <StatusBadge status={booking.status} /></span>}
                subtitle={`Booked on ${formatDate(booking.createdAt, 'DD MMM YYYY, hh:mm A')}`}
                actions={next.map(s => (
                    <button key={s} type="button" className={`btn btn-sm ${ACTION_LABELS[s]?.className}`} disabled={isUpdating} onClick={() => changeStatus(s)}>
                        <i className={`bi ${ACTION_LABELS[s]?.icon} me-1`}></i>{ACTION_LABELS[s]?.label}
                    </button>
                ))}
            />

            <div className="row g-4">
                <div className="col-xl-8">
                    <div className="hp-card mb-4">
                        <div className="hp-card-head"><h3 className="hp-card-title">Service</h3></div>
                        <div className="hp-card-body d-flex gap-4 flex-wrap">
                            {service.images?.[0] && (
                                <img src={service.images[0]} alt={service.name} className="rounded" style={{ width: 120, height: 90, objectFit: 'cover' }}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            )}
                            <div className="flex-grow-1" style={{ minWidth: 220 }}>
                                <div className="fs-5 fw-bold text-dark">{service.name || '—'}</div>
                                <div className="text-muted mb-3">{service.category_id?.title}{service.sub_category_id ? ` › ${service.sub_category_id.title}` : ''}</div>
                                {service.short_desc && <p className="text-gray-700 mb-3">{service.short_desc}</p>}
                            </div>
                        </div>
                    </div>

                    <div className="hp-card mb-4">
                        <div className="hp-card-head"><h3 className="hp-card-title">Schedule & location</h3></div>
                        <div className="hp-card-body">
                            <Row label="Date">{booking.date ? formatDate(booking.date, 'dddd, DD MMM YYYY') : null}</Row>
                            <Row label="Time">{booking.time}</Row>
                            <Row label="Location">{booking.location}</Row>
                            {property && (
                                <>
                                    <Row label="Property">{[property.building_name, property.unit_no && `Unit ${property.unit_no}`].filter(Boolean).join(', ')}</Row>
                                    <Row label="Area / Community">{property.area}</Row>
                                    <Row label="Unit type">{property.unit_type}</Row>
                                    {property.contact_number && <Row label="Property contact">{property.phone_code} {property.contact_number}</Row>}
                                </>
                            )}
                            {address && <Row label="Address">{address}</Row>}
                            <Row label="Notes">{booking.notes}</Row>
                        </div>
                    </div>

                    {Array.isArray(booking.media_files) && booking.media_files.length > 0 && (
                        <div className="hp-card mb-4">
                            <div className="hp-card-head"><h3 className="hp-card-title">Photos & files from the customer</h3></div>
                            <div className="hp-card-body d-flex flex-wrap gap-3">
                                {booking.media_files.map((mf, i) => (
                                    <a key={i} href={mf.file || mf.url} target="_blank" rel="noreferrer">
                                        <span className="btn btn-sm hp-btn-outline">
                                            <i className={`bi ${mf.type === 'image' ? 'bi-image' : mf.type === 'video' ? 'bi-camera-video' : 'bi-paperclip'} me-1`}></i>
                                            {mf.name || `Attachment ${i + 1}`}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-xl-4">
                    <div className="hp-card mb-4">
                        <div className="hp-card-head"><h3 className="hp-card-title">Progress</h3></div>
                        <div className="hp-card-body">
                            {booking.status === 'Cancelled' && (
                                <div className="alert alert-danger py-2 px-3 fs-7 mb-4">This booking was cancelled.</div>
                            )}
                            <ul className="hp-timeline">
                                {(booking.timeline || []).map(step => (
                                    <li key={step.status} className={step.is_done ? 'done' : ''}>
                                        <span className="hp-tl-dot">{step.is_done && <i className="bi bi-check"></i>}</span>
                                        <div className="hp-tl-title">{step.status}</div>
                                        <div className="hp-tl-time">{step.created_at ? formatDate(step.created_at, 'DD MMM YYYY, hh:mm A') : (step.is_done ? 'Done' : 'Pending')}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="hp-card mb-4">
                        <div className="hp-card-head"><h3 className="hp-card-title">Customer</h3></div>
                        <div className="hp-card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <Avatar src={customer.picture} name={customer.name} size={44} />
                                <div>
                                    <div className="fw-bold text-dark">{customer.name || '—'}</div>
                                    <div className="text-muted fs-7">{customer.email}</div>
                                </div>
                            </div>
                            {customer.phone && (
                                <a href={`tel:${customer.phone_code || ''}${customer.phone}`} className="btn btn-sm hp-btn-outline w-100">
                                    <i className="bi bi-telephone me-1"></i> {customer.phone_code} {customer.phone}
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="hp-card">
                        <div className="hp-card-head"><h3 className="hp-card-title">Payment</h3></div>
                        <div className="hp-card-body">
                            <Row label="Sub total">{money(booking.sub_total)}</Row>
                            <Row label="Service charges">{money(booking.service_charges)}</Row>
                            <Row label="Paid">{money(booking.pay_amount)}</Row>
                            <Row label="Method">{booking.pay_method}</Row>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalBookingDetails;
